// Scientific name → English common name for Fishial species.
//
// Ported server-side at backend/app/species/common_names.py (kept aligned by
// hand — regenerate both together) for the submission reply flow, which has
// no frontend to defer to.
//
// Complete Fishial V10 class list (865 species), generated 2026-09-16 from
// https://docs.fishial.ai/api/specieslist, plus one legacy taxon
// (Micropterus salmoides, pre-2023 name for largemouth bass). Six values are
// curated overrides of Fishial's official wording (Atlantic tarpon, Gray
// snapper, Sockeye salmon, Dolly Varden, Yellow perch, Largemouth bass) — when
// regenerating, re-apply them. Keys must match the classifier's output strings
// verbatim (a few are hybrids or non-fish, e.g. "... X ...", "sepia
// officinalis"). Purely display-side: the API keeps the canonical scientific
// name + species_id, and `commonName` returns undefined for anything unmapped.

const COMMON_NAMES: Record<string, string> = {
  'Ablennes hians': 'Flat needlefish',
  'Abramis brama': 'Freshwater bream',
  'Abudefduf saxatilis': 'Sergeant-major',
  'Abudefduf sordidus': 'Blackspot sergeant',
  'Acantharchus pomotis': 'Mud sunfish',
  'Acanthemblemaria johnsoni': 'Black barnacle blenny',
  'Acanthemblemaria spinosa': 'Spinyhead blenny',
  'Acanthocybium solandri': 'Wahoo',
  'Acanthopagrus australis': 'Yellowfin bream',
  'Acanthopagrus butcheri': 'Southern Black bream',
  'Acanthopagrus latus': 'Yellowfin seabream',
  'Acanthostracion quadricornis': 'Scrawled cowfish',
  'Acanthurus chirurgus': 'Doctorfish',
  'Acanthurus coeruleus': 'Blue tang surgeonfish',
  'Acanthurus dussumieri': 'Eyestripe surgeonfish',
  'Achoerodus viridis': 'Eastern blue groper',
  'Acipenser brevirostrum': 'Shortnose sturgeon',
  'Acipenser fulvescens': 'Lake sturgeon',
  'Acipenser sturio': 'Sturgeon',
  'Acipenser transmontanus': 'White sturgeon',
  'Acroteriobatus annulatus': 'Lesser sandshark',
  'Aetobatus narinari': 'Whitespotted eagle ray',
  'Aetomylaeus bovinus': 'Bull ray',
  'Albula vulpes': 'Bonefish',
  'Alburnus alburnus': 'Bleak',
  'Aldrichetta forsteri': 'Yellow-eye mullet',
  'Alectis ciliaris': 'African pompano',
  'Alopias vulpinus': 'Thresher',
  'Alosa aestivalis': 'Blueback shad',
  'Alosa chrysochloris': 'Skipjack shad',
  'Alosa mediocris': 'Hickory shad',
  'Alosa pseudoharengus': 'Alewife',
  'Alosa sapidissima': 'American shad',
  'Aluterus monoceros': 'Unicorn leatherjacket filefish',
  'Aluterus schoepfii': 'Orange filefish',
  'Aluterus scriptus': 'Scribbled leatherjacket filefish',
  'Ambloplites ariommus': 'Shadow bass',
  'Ambloplites rupestris': 'Rock bass',
  'Ameiurus catus': 'White catfish',
  'Ameiurus melas': 'Black bullhead',
  'Ameiurus natalis': 'Yellow bullhead',
  'Ameiurus nebulosus': 'Brown bullhead',
  'Ameiurus platycephalus': 'Flat bullhead',
  'Ameiurus serracanthus': 'Spotted bullhead',
  'Amia calva': 'Bowfin',
  'Amniataba percoides': 'Barred grunter',
  'Amphilophus citrinellus': 'Midas cichlid',
  'Amphilophus labiatus': 'Red devil',
  'Amphiprion ocellaris': 'Clown anemonefish',
  'Amphiprion percula': 'Orange clownfish',
  'Amphistichus argenteus': 'Barred surfperch',
  'Amphistichus rhodoterus': 'Redtail surfperch',
  'Anchoa mitchilli': 'Bay anchovy',
  'Anguilla anguilla': 'European eel',
  'Anguilla australis': 'Short-finned eel',
  'Anguilla reinhardtii': 'Speckled longfin eel',
  'Anguilla rostrata': 'American eel',
  'Anisotremus davidsonii': 'Xantic sargo',
  'Anisotremus surinamensis': 'Black margate',
  'Anisotremus virginicus': 'Porkfish',
  'Aphredoderus sayanus': 'Pirate perch',
  'Aplodinotus grunniens': 'Freshwater drum',
  'Aprion virescens': 'Green jobfish',
  'Aprognathodon platyventris': 'Stripe eel',
  'Aptychotrema vincentiana': 'Western shovelnose ray',
  'Archosargus probatocephalus': 'Sheepshead',
  'Archosargus rhomboidalis': 'Western Atlantic seabream',
  'Argyrosomus coronus': 'Dusky kob',
  'Argyrosomus japonicus': 'Japanese meagre',
  'Ariopsis felis': 'Hardhead sea catfish',
  'Arothron hispidus': 'White-spotted puffer',
  'Arothron stellatus': 'Stellate puffer',
  'Arripis trutta': 'Australian salmon',
  'Arripis truttacea': 'Western Australian salmon',
  'Astronotus ocellatus': 'Oscar',
  'Atherinops affinis': 'Topsmelt silverside',
  'Atherinopsis californiensis': 'Jack silverside',
  'Atractoscion nobilis': 'White Seabass (weakfish)',
  'Atractosteus spatula': 'Alligator gar',
  'Atule mate': 'Yellowtail scad',
  'Atypichthys strigatus': 'Australian mado',
  'Aulostomus maculatus': 'Trumpetfish',
  'Auxis thazard': 'Frigate tuna',
  'Bagre marinus': 'Gafftopsail sea catfish',
  'Bairdiella chrysoura': 'Goldtail croaker',
  'Balistapus undulatus': 'Orange-lined triggerfish',
  'Balistes capriscus': 'Grey triggerfish',
  'Balistes vetula': 'Queen triggerfish',
  'Balistoides viridescens': 'Titan triggerfish',
  'Barbonymus gonionotus': 'Silver barb',
  'Barbus barbus': 'Barbel',
  'Belone belone': 'Garfish',
  'Bidyanus bidyanus': 'Silver perch (Australia)',
  'Blicca bjoerkna': 'White bream',
  'Bodianus rufus': 'Spanish hogfish',
  'Boops boops': 'Bogue',
  'Brachaelurus waddi': 'Blind shark',
  'Brevoortia patronus': 'Gulf menhaden',
  'Brevoortia tyrannus': 'Atlantic menhaden',
  'Calamus bajonado': 'Jolthead porgy',
  'Calamus calamus': 'Saucereye porgy',
  'Calamus proridens': 'Littlehead porgy',
  'Campostoma anomalum': 'Central stoneroller',
  'Cantherhines pullus': 'Orangespotted filefish',
  'Canthidermis sufflamen': 'Ocean triggerfish',
  'Canthigaster rostrata': 'Caribbean sharpnose-puffer',
  'Caranx bartholomaei': 'Yellow jack',
  'Caranx caninus': 'Pacific crevalle jack',
  'Caranx crysos': 'Blue runner',
  'Caranx hippos': 'Crevalle jack',
  'Caranx ignobilis': 'Giant trevally',
  'Caranx latus': 'Horse-eye jack',
  'Caranx lugubris': 'Black jack',
  'Caranx melampygus': 'Bluefin trevally',
  'Caranx papuensis': 'Brassy trevally',
  'Caranx ruber': 'Bar jack',
  'Caranx sexfasciatus': 'Bigeye trevally',
  'Carassius auratus': 'Goldfish',
  'Carassius carassius': 'Crucian carp',
  'Carassius gibelio': 'Prussian carp',
  'Carcharhinus acronotus': 'Blacknose shark',
  'Carcharhinus brachyurus': 'Copper shark',
  'Carcharhinus brevipinna': 'Spinner shark',
  'Carcharhinus falciformis': 'Silky shark',
  'Carcharhinus isodon': 'Finetooth shark',
  'Carcharhinus leucas': 'Bull shark',
  'Carcharhinus limbatus': 'Blacktip shark',
  'Carcharhinus melanopterus': 'Blacktip reef shark',
  'Carcharhinus obscurus': 'Dusky shark',
  'Carcharhinus perezii': 'Caribbean reef shark',
  'Carcharhinus plumbeus': 'Sandbar shark',
  'Carcharhinus sorrah': 'Spot-tail shark',
  'Carcharias taurus': 'Sand tiger shark',
  'Carcharodon carcharias': 'Great white shark',
  'Carpiodes carpio': 'River carpsucker',
  'Carpiodes cyprinus': 'Quillback',
  'Carpiodes velifer': 'Highfin carpsucker',
  'Catostomus catostomus': 'Longnose sucker',
  'Catostomus commersonii': 'White sucker',
  'Catostomus macrocheilus': 'Largescale sucker',
  'Caulolatilus chrysops': 'Atlantic goldeneye tilefish',
  'Caulolatilus princeps': 'Ocean whitefish',
  'Cebidichthys violaceus': 'Monkeyface prickleback',
  'Centrarchus macropterus': 'Flier',
  'Centropomus parallelus': 'Fat snook',
  'Centropomus undecimalis': 'Common snook',
  'Centropristis striata': 'Black seabass',
  'Cephalopholis argus': 'Peacock hind',
  'Cephalopholis cruentata': 'Graysby',
  'Cephalopholis fulva': 'Coney',
  'Cephalopholis miniata': 'Coral hind',
  'Cephalopholis sonnerati': 'Tomato hind',
  'Cephalopholis taeniops': 'Bluespotted seabass',
  'Chaenopsis limbaughi': 'Yellowface pikeblenny',
  'Chaetodipterus faber': 'Atlantic spadefish',
  'Chaetodon capistratus': 'Foureye butterflyfish',
  'Chaetodon ephippium': 'Saddle butterflyfish',
  'Channa argus': 'Northern snakehead',
  'Channa aurolineata': 'Bullseye Snakehead',
  'Channa maculata': 'Blotched snakehead',
  'Channa marulioides': 'Orange snakehead',
  'Channa marulius': 'Great snakehead',
  'Channa micropeltes': 'Indonesian snakehead',
  'Channa pseudomarulius': 'Orangespotted snakehead',
  'Channa striata': 'Striped snakehead',
  'Channomuraena vittata': 'Broadbanded moray',
  'Chanos chanos': 'Milkfish',
  'Cheilinus trilobatus': 'Tripletail wrasse',
  'Cheilinus undulatus': 'Humphead wrasse',
  'Cheilio inermis': 'Cigar wrasse',
  'Chelidonichthys cuculus': 'Red gurnard',
  'Chelidonichthys kumu': 'Bluefin gurnard',
  'Chelidonichthys lucerna': 'Tub gurnard',
  'Chelon labrosus': 'Thicklip grey mullet',
  'Chilomycterus schoepfii': 'Striped burrfish',
  'Chitala ornata': 'Clown knifefish',
  'Chloroscombrus chrysurus': 'Atlantic bumper',
  'Chlorurus sordidus': 'Daisy parrotfish',
  'Chondrostoma nasus': 'Common nase',
  'Chromis chromis': 'Damselfish',
  'Chromis viridis': 'Blue green damselfish',
  'Chrysoblephus laticeps': 'Roman seabream',
  'Cichla monoculus': 'Popoca peacock',
  'Cichla ocellaris': 'Butterfly peacock bass (Peacock cichlid)',
  'Cichla temensis': 'Speckled pavon',
  'Cirrhinus molitorella': 'Mud carp',
  'Cirrhitus pinnulatus': 'Stocky hawkfish',
  'Citharichthys sordidus': 'Pacific sanddab',
  'Clarias batrachus': 'Philippine catfish',
  'Clarias gariepinus': 'North African catfish',
  'Clarias ngamensis': 'Blunt-toothed African catfish',
  'Clepticus parrae': 'Creole wrasse',
  'Clinocottus analis': 'Woolly sculpin',
  'Clupea harengus': 'Atlantic herring',
  'Clupea pallasii': 'Pacific herring',
  'Cnidoglanis macrocephalus': 'Estuary cobbler',
  'Conger conger': 'European conger',
  'Conger verreauxi': 'Conger',
  'Coptodon rendalli': 'Redbreast tilapia',
  'Coptodon zillii': 'Redbelly tilapia',
  'Coregonus artedi': 'Cisco',
  'Coregonus clupeaformis': 'Lake whitefish',
  'Coris julis': 'Mediterranean rainbow wrasse',
  'Coryphaena hippurus': 'Mahi Mahi',
  'Coryphopterus dicrus': 'Colon goby',
  'Cottus bairdii': 'Mottled sculpin',
  'Cottus gobio': 'Bullhead',
  'Couesius plumbeus': 'Lake chub',
  'Crenicichla lepidota': 'Pike cichlid',
  'Ctenolabrus rupestris': 'Goldsinny-wrasse',
  'Ctenopharyngodon idella': 'Grass carp',
  'Cymatogaster aggregata': 'Shiner perch',
  'Cynoscion arenarius': 'Sand weakfish',
  'Cynoscion nebulosus': 'Spotted seatrout',
  'Cynoscion regalis': 'Squeteague',
  'Cyprinella lutrensis': 'Red shiner',
  'Cyprinella spiloptera': 'Spotfin shiner',
  'Cyprinella venusta': 'Blacktail shiner',
  'Cyprinus carpio': 'Common carp',
  'Cyprinus carpio carpio': 'Mirror Carp',
  'Cyprinus rubrofuscus': 'Koi Carp',
  'Dactylopterus volitans': 'Flying gurnard',
  'Dasyatis pastinaca': 'Common stingray',
  'Decapterus macarellus': 'Mackerel scad',
  'Dentex dentex': 'Common dentex',
  'Diagramma pictum': 'Painted sweetlips',
  'Dicentrarchus labrax': 'European seabass',
  'Dicentrarchus punctatus': 'Spotted seabass',
  'Dichistius capensis': 'Galjoen',
  'Diodon holocanthus': 'Longspined porcupinefish',
  'Diodon hystrix': 'Spot-fin porcupinefish',
  'Diplectrum formosum': 'Sand perch',
  'Diplodus annularis': 'Annular seabream',
  'Diplodus capensis': 'Cape white seabream',
  'Diplodus cervinus': 'Zebra seabream',
  'Diplodus holbrookii': 'Spottail seabream',
  'Diplodus puntazzo': 'Sharpsnout seabream',
  'Diplodus sargus': 'White seabream',
  'Diplodus vulgaris': 'Common two-banded seabream',
  'Dorosoma cepedianum': 'American gizzard shad',
  'Dorosoma petenense': 'Threadfin shad',
  'Drepane punctata': 'Spotted sicklefish',
  'Echeneis naucrates': 'Live sharksucker',
  'Echeneis neucratoides': 'Whitefin sharksucker',
  'Echidna catenata': 'Chain moray',
  'Echiophis intertinctus': 'Spotted spoon-nose eel',
  'Elagatis bipinnulata': 'Rainbow runner',
  'Eleutheronema tetradactylum': 'Fourfinger threadfin',
  'Ellochelon vaigiensis': 'Squaretail mullet',
  'Elops saurus': 'Ladyfish',
  'Embiotoca jacksoni': 'Black perch',
  'Embiotoca lateralis': 'Striped seaperch',
  'Enneacanthus chaetodon': 'Blackbanded sunfish',
  'Enneacanthus gloriosus': 'Bluespotted sunfish',
  'Enneacanthus obesus': 'Banded sunfish',
  'Ephippion guttifer': 'Prickly puffer',
  'Epinephelus adscensionis': 'Rock hind',
  'Epinephelus analogus': 'Spotted grouper',
  'Epinephelus coioides': 'Orange-spotted grouper',
  'Epinephelus fasciatus': 'Blacktip grouper',
  'Epinephelus fuscoguttatus': 'Brown-marbled grouper',
  'Epinephelus guttatus': 'Red hind',
  'Epinephelus itajara': 'Atlantic goliath grouper',
  'Epinephelus labriformis': 'Starry grouper',
  'Epinephelus lanceolatus': 'Giant grouper',
  'Epinephelus malabaricus': 'Malabar grouper',
  'Epinephelus marginatus': 'Dusky grouper',
  'Epinephelus merra': 'Honeycomb grouper',
  'Epinephelus morio': 'Red grouper',
  'Epinephelus polyphekadion': 'Camouflage grouper',
  'Epinephelus striatus': 'Nassau grouper',
  'Epinephelus tauvina': 'Greasy grouper',
  'Epinephelus tukula': 'Potato grouper',
  'Erimyzon oblongus': 'Eastern creek chubsucker',
  'Esox americanus': 'Redfin pickerel',
  'Esox americanus vermiculatus': 'Grass pickerel',
  'Esox lucius': 'Northern pike',
  'Esox masquinongy': 'Muskellunge',
  'Esox masquinongy punctulatus': 'Spotted Muskellunge',
  'Esox masquinongy X Esox lucius': 'Tiger Muskellunge',
  'Esox niger': 'Chain pickerel',
  'Etelis oculatus': 'Queen snapper',
  'Etheostoma caeruleum': 'Rainbow darter',
  'Etropus crossotus': 'Fringed flounder',
  'Eucinostomus gula': 'Jenny mojarra',
  'Eugerres plumieri': 'Striped mojarra',
  'Euthynnus affinis': 'Kawakawa',
  'Euthynnus alletteratus': 'Little tunny',
  'Fundulus chrysotus': 'Golden topminnow',
  'Fundulus diaphanus': 'Banded killifish',
  'Fundulus olivaceus': 'Blackspotted topminnow',
  'Gadus macrocephalus': 'Pacific cod',
  'Gadus morhua': 'Atlantic cod',
  'Galeichthys feliceps': 'White barbel',
  'Galeocerdo cuvier': 'Tiger shark',
  'Galeorhinus galeus': 'Tope shark',
  'Gambusia affinis': 'Mosquitofish',
  'Gasterosteus aculeatus': 'Three-spined stickleback',
  'Genyonemus lineatus': 'White croaker',
  'Gerres cinereus': 'Yellow fin mojarra',
  'Ginglymostoma cirratum': 'Nurse shark',
  'Girella elevata': 'Black drummer',
  'Girella nigricans': 'Opaleye',
  'Girella tricuspidata': 'Parore',
  'Glaucosoma hebraicum': 'West Australian dhufish',
  'Gnathanodon speciosus': 'Golden trevally',
  'Gobiesox rhessodon': 'California clingfish',
  'Gobio gobio': 'Gudgeon',
  'Gobioclinus filamentosus': 'Quillfin blenny',
  'Gobius paganellus': 'Rock goby',
  'Gymnocephalus cernua': 'Ruffe',
  'Gymnosarda unicolor': 'Dogtooth tuna',
  'Gymnothorax funebris': 'Green moray',
  'Gymnothorax miliaris': 'Goldentail moray',
  'Gymnothorax moringa': 'Spotted moray',
  'Gymnura altavela': 'Spiny butterfly ray',
  'Haemulon album': 'White margate',
  'Haemulon aurolineatum': 'Tomtate grunt',
  'Haemulon chrysargyreum': 'Smallmouth grunt',
  'Haemulon flavolineatum': 'French grunt',
  'Haemulon parra': 'Sailor\'s grunt',
  'Haemulon plumierii': 'White grunt',
  'Haemulon sciurus': 'Bluestriped grunt',
  'Halichoeres bivittatus': 'Slippery dick',
  'Halichoeres garnoti': 'Yellowhead wrasse',
  'Halichoeres maculipinna': 'Clown wrasse',
  'Halichoeres radiatus': 'Puddingwife wrasse',
  'Halichoeres semicinctus': 'Rock wrasse',
  'Hampala macrolepidota': 'Hampala barb',
  'Haploblepharus edwardsii': 'Puffadder shyshark',
  'Helicolenus dactylopterus': 'Blackbelly rosefish',
  'Hemigymnus melapterus': 'Blackeye thicklip',
  'Hephaestus fuliginosus': 'Sooty grunter',
  'Herichthys cyanoguttatus': 'Rio Grande cichlid',
  'Heteroconger longissimus': 'Brown garden eel',
  'Heterodontus francisci': 'Horn shark',
  'Heterodontus portusjacksoni': 'Port Jackson shark',
  'Heterostichus rostratus': 'Giant kelpfish',
  'Hexagrammos decagrammus': 'Kelp greenling',
  'Hexagrammos stelleri': 'Whitespotted greenling',
  'Himantura uarnak': 'Honeycomb stingray',
  'Hiodon alosoides': 'Goldeye',
  'Hiodon tergisus': 'Mooneye',
  'Hippoglossoides elassodon': 'Flathead sole',
  'Hippoglossus stenolepis': 'Pacific halibut',
  'Holacanthus bermudensis': 'Angelfish',
  'Holacanthus ciliaris': 'Queen angelfish',
  'Holocentrus adscensionis': 'Squirrelfish',
  'Hoplias malabaricus': 'Trahira',
  'Hoplopagrus guentherii': 'Mexican barred snapper',
  'Hoplosternum littorale': 'Atipa',
  'Hybognathus hankinsoni': 'Brassy minnow',
  'Hydrocynus vittatus': 'Ndweshi (African Tiger fish)',
  'Hypanus americanus': 'Southern stingray',
  'Hypanus sabinus': 'Atlantic stingray',
  'Hypanus say': 'Bluntnose stingray',
  'Hypentelium etowanum': 'Alabama hog sucker',
  'Hypentelium nigricans': 'Northern hog sucker',
  'Hyperprosopon argenteum': 'Walleye surfperch',
  'Hyperprosopon ellipticum': 'Silver surfperch',
  'Hypomesus pretiosus': 'Surf smelt',
  'Hypophthalmichthys molitrix': 'Silver carp',
  'Hypophthalmichthys nobilis': 'Bighead carp',
  'Hypoplectrus guttavarius': 'Shy hamlet',
  'Hyporhamphus melanochir': 'Southern garfish',
  'Hyporthodus niveatus': 'Snowy grouper',
  'Hypsypops rubicundus': 'Garibaldi damselfish',
  'Ictalurus furcatus': 'Blue catfish',
  'Ictalurus punctatus': 'Channel catfish',
  'Ictiobus bubalus': 'Smallmouth buffalo',
  'Ictiobus cyprinellus': 'Bigmouth buffalo',
  'Ictiobus niger': 'Black buffalo',
  'Istiompax indica': 'Black marlin',
  'Istiophorus albicans': 'Atlantic Sailfish',
  'Istiophorus platypterus': 'Indo-Pacific sailfish',
  'Isurus oxyrinchus': 'Shortfin mako',
  'Kajikia audax': 'Striped marlin',
  'Katsuwonus pelamis': 'Skipjack tuna',
  'Kuhlia rupestris': 'Rock flagtail',
  'Kyphosus sectatrix': 'Bermuda sea chub',
  'Kyphosus vaigiensis': 'Brassy chub',
  'Labeo capensis': 'Orange river mudfish',
  'Labeo rohita': 'Roho labeo',
  'Labeobarbus aeneus': 'Smallmouth yellowfish',
  'Labeobarbus marequensis': 'Largescale yellowfish',
  'Labroides dimidiatus': 'Bluestreak cleaner wrasse',
  'Labrus bergylta': 'Ballan wrasse',
  'Labrus mixtus': 'Cuckoo wrasse',
  'Lachnolaimus maximus': 'Hogfish',
  'Lagocephalus laevigatus': 'Smooth puffer',
  'Lagocephalus lagocephalus': 'Oceanic puffer',
  'Lagodon rhomboides': 'Pinfish',
  'Lampris guttatus': 'Opah',
  'Lateolabrax japonicus': 'Japanese seabass',
  'Lates calcarifer': 'Barramundi',
  'Leiopotherapon unicolor': 'Spangled perch',
  'Leiostomus xanthurus': 'Spot croaker',
  'Lepisosteus oculatus': 'Spotted gar',
  'Lepisosteus osseus': 'Longnose gar',
  'Lepisosteus platostomus': 'Shortnose gar',
  'Lepisosteus platyrhincus': 'Florida gar',
  'Lepomis auritus': 'Redbreast sunfish',
  'Lepomis cyanellus': 'Green sunfish',
  'Lepomis gibbosus': 'Pumpkinseed',
  'Lepomis gulosus': 'Warmouth',
  'Lepomis humilis': 'Orangespotted sunfish',
  'Lepomis macrochirus': 'Bluegill',
  'Lepomis marginatus': 'Dollar sunfish',
  'Lepomis megalotis': 'Longear sunfish',
  'Lepomis microlophus': 'Redear sunfish',
  'Lepomis miniatus': 'Redspotted sunfish',
  'Lepomis peltastes': 'Northern sunfish',
  'Lepomis symmetricus': 'Bantam sunfish',
  'Leptocottus armatus': 'Pacific staghorn sculpin',
  'Lethrinus laticaudis': 'Grass emperor',
  'Lethrinus lentjan': 'Pink ear emperor',
  'Lethrinus miniatus': 'Trumpet emperor',
  'Lethrinus nebulosus': 'Spangled emperor',
  'Lethrinus obsoletus': 'Orange-striped emperor',
  'Leuciscus aspius': 'Asp',
  'Leuciscus idus': 'Ide',
  'Leuciscus leuciscus': 'Common dace',
  'Lichia amia': 'Leerfish',
  'Limanda limanda': 'Common dab',
  'Lipophrys pholis': 'Shanny',
  'Lithognathus lithognathus': 'White steenbras',
  'Lithognathus mormyrus': 'Sand steenbras',
  'Lobotes surinamensis': 'Tripletail',
  'Loligo vulgaris': 'Common Squid',
  'Lota lota': 'Burbot',
  'Luciopimelodus pati': 'Pati',
  'Lutjanus agennes': 'African red snapper',
  'Lutjanus analis': 'Mutton snapper',
  'Lutjanus apodus': 'Schoolmaster snapper',
  'Lutjanus argentimaculatus': 'Mangrove red snapper',
  'Lutjanus argentiventris': 'Yellow snapper',
  'Lutjanus bohar': 'Two-spot red snapper',
  'Lutjanus buccanella': 'Blackfin snapper',
  'Lutjanus campechanus': 'Northern red snapper',
  'Lutjanus carponotatus': 'Spanish flag snapper',
  'Lutjanus cyanopterus': 'Cubera snapper',
  'Lutjanus decussatus': 'Checkered snapper',
  'Lutjanus ehrenbergii': 'Blackspot snapper',
  'Lutjanus fulviflamma': 'Dory snapper',
  'Lutjanus fulvus': 'Blacktail snapper',
  'Lutjanus gibbus': 'Humpback red snapper',
  'Lutjanus griseus': 'Gray snapper',
  'Lutjanus jocu': 'Dog snapper',
  'Lutjanus johnii': 'John\'s snapper',
  'Lutjanus kasmira': 'Common bluestripe snapper',
  'Lutjanus mahogoni': 'Mahogany snapper',
  'Lutjanus monostigma': 'One-spot snapper',
  'Lutjanus novemfasciatus': 'Pacific dog snapper',
  'Lutjanus purpureus': 'Southern red snapper',
  'Lutjanus rivulatus': 'Blubberlip snapper',
  'Lutjanus russellii': 'Russell\'s snapper',
  'Lutjanus sebae': 'Emperor red snapper',
  'Lutjanus synagris': 'Lane snapper',
  'Lutjanus vitta': 'Brownstripe red snapper',
  'Lutjanus vivanus': 'Silk snapper',
  'Luxilus cerasinus': 'Crescent shiner',
  'Luxilus chrysocephalus': 'Striped shiner',
  'Luxilus cornutus': 'Common shiner',
  'Maccullochella macquariensis': 'Trout cod',
  'Maccullochella peelii': 'Murray cod',
  'Macquaria ambigua': 'Golden perch',
  'Makaira nigricans': 'Blue marlin',
  'Mayaheros urophthalmus': 'Mayan Cichlid',
  'Megalaspis cordyla': 'Torpedo scad',
  'Megalops atlanticus': 'Atlantic tarpon',
  'Megalops cyprinoides': 'Indo-Pacific tarpon',
  'Melanogrammus aeglefinus': 'Haddock',
  'Melichthys niger': 'Black triggerfish',
  'Menidia menidia': 'Atlantic silverside',
  'Menticirrhus americanus': 'Southern kingcroaker',
  'Menticirrhus littoralis': 'Gulf kingcroaker',
  'Menticirrhus saxatilis': 'Northern kingfish',
  'Menticirrhus undulatus': 'California kingcroaker',
  'Merlangius merlangus': 'Whiting',
  'Meuschenia freycineti': 'Six-spined leatherjacket',
  'Micropogonias undulatus': 'Atlantic croaker',
  'Micropterus cataractae': 'Shoal bass',
  'Micropterus coosae': 'Redeye bass',
  'Micropterus dolomieu': 'Smallmouth bass',
  'Micropterus floridanus': 'Florida Bass',
  'Micropterus henshalli': 'Alabama bass',
  'Micropterus nigricans': 'Largemouth bass',
  'Micropterus notius': 'Suwannee bass',
  'Micropterus punctulatus': 'Spotted bass',
  'Micropterus salmoides': 'Largemouth bass',
  'Micropterus tallapoosae': 'Tallapoosa Bass',
  'Micropterus treculii': 'Guadalupe bass',
  'Minytrema melanops': 'Spotted sucker',
  'Mola mola': 'Ocean sunfish',
  'Monacanthus chinensis': 'Fan-bellied leatherjacket',
  'Monodactylus argenteus': 'Silver moony',
  'Morone americana': 'White perch',
  'Morone chrysops': 'White bass',
  'Morone chrysops X Morone saxatilis': 'Wiper (Hybrid striped bass)',
  'Morone mississippiensis': 'Yellow bass',
  'Morone saxatilis': 'Striped bass',
  'Moxostoma anisurum': 'Silver redhorse',
  'Moxostoma breviceps': 'Smallmouth redhorse',
  'Moxostoma carinatum': 'River redhorse',
  'Moxostoma duquesnei': 'Black redhorse',
  'Moxostoma erythrurum': 'Golden redhorse',
  'Moxostoma macrolepidotum': 'Shorthead redhorse',
  'Moxostoma valenciennesi': 'Greater redhorse',
  'Mugil cephalus': 'Flathead grey mullet',
  'Mugil curema': 'White mullet',
  'Mulloidichthys flavolineatus': 'Yellowstripe goatfish',
  'Mulloidichthys martinicus': 'Yellow goatfish',
  'Mullus barbatus': 'Red mullet',
  'Mullus surmuletus': 'Surmullet',
  'Muraenesox bagio': 'Common pike conger',
  'Mustelus antarcticus': 'Gummy shark',
  'Mustelus asterias': 'Starry smooth-hound',
  'Mustelus canis': 'Dusky smooth-hound',
  'Mustelus lenticulatus': 'Spotted estuary smooth-hound',
  'Mustelus mustelus': 'Smooth-hound',
  'Mycteroperca bonaci': 'Black grouper',
  'Mycteroperca microlepis': 'Gag',
  'Mycteroperca phenax': 'Scamp',
  'Mycteroperca rubra': 'Mottled grouper',
  'Mycteroperca tigris': 'Tiger grouper',
  'Mycteroperca venenosa': 'Yellowfin grouper',
  'Myliobatis aquila': 'Common eagle ray',
  'Myliobatis californica': 'Bat eagle ray',
  'Myliobatis freminvillei': 'Bullnose eagle ray',
  'Myoxocephalus octodecemspinosus': 'Longhorn sculpin',
  'Myrichthys breviceps': 'Sharptail eel',
  'Myrichthys ocellatus': 'Goldspotted eel',
  'Myripristis berndti': 'Blotcheye soldierfish',
  'Naso unicornis': 'Bluespine unicornfish',
  'Nebrius ferrugineus': 'Tawny nurse shark',
  'Negaprion brevirostris': 'Lemon shark',
  'Nemadactylus macropterus': 'Tarakihi',
  'Nematistius pectoralis': 'Roosterfish',
  'Neogobius melanostomus': 'Round goby',
  'Nocomis biguttatus': 'Hornyhead chub',
  'Nocomis leptocephalus': 'Bluehead chub',
  'Nocomis micropogon': 'River chub',
  'Notemigonus crysoleucas': 'Golden shiner',
  'Notolabrus celidotus': 'Spotty',
  'Notolabrus fucicola': 'Yellow-saddled wrasse',
  'Notolabrus gymnogenis': 'Crimsonband wrasse',
  'Notolabrus tetricus': 'Blue-throated wrasse',
  'Notorynchus cepedianus': 'Broadnose sevengill shark',
  'Notropis amabilis': 'Texas shiner',
  'Notropis atherinoides': 'Emerald shiner',
  'Notropis hudsonius': 'Spottail shiner',
  'Notropis stramineus': 'Sand shiner',
  'Noturus flavus': 'Stonecat',
  'Oblada melanurus': 'Saddled seabream',
  'Ocyurus chrysurus': 'Yellowtail snapper',
  'Oligoplites saurus': 'Leatherjacket',
  'Oncorhynchus aguabonita': 'Golden trout',
  'Oncorhynchus clarkii': 'Cutthroat trout',
  'Oncorhynchus clarkii bouvieri': 'Yellowstone Cutthroat Trout',
  'Oncorhynchus clarkii clarkii': 'Coastal Cutthroat Trout',
  'Oncorhynchus clarkii lewisi': 'Westslope Cutthroat Trout',
  'Oncorhynchus gorbuscha': 'Pink salmon',
  'Oncorhynchus keta': 'Chum salmon',
  'Oncorhynchus kisutch': 'Coho salmon',
  'Oncorhynchus mykiss': 'Rainbow trout',
  'Oncorhynchus mykiss X Color variant2': 'Palomino Trout',
  'Oncorhynchus mykiss X Oncorhynchus clarkii': 'Cutbow Trout',
  'Oncorhynchus nerka': 'Sockeye salmon',
  'Oncorhynchus tshawytscha': 'Chinook salmon',
  'Ophichthus ophis': 'Spotted snake eel',
  'Ophiodon elongatus': 'Lingcod',
  'Opisthonema oglinum': 'Atlantic thread herring',
  'Opsanus beta': 'Gulf toadfish',
  'Opsanus tau': 'Oyster toadfish',
  'Oreochromis andersonii': 'Three spotted tilapia',
  'Oreochromis aureus': 'Blue tilapia',
  'Oreochromis macrochir': 'Longfin tilapia',
  'Oreochromis mossambicus': 'Mozambique tilapia',
  'Oreochromis niloticus': 'Nile tilapia',
  'Orthopristis chrysoptera': 'Pigfish',
  'Osmerus mordax': 'Rainbow smelt',
  'Oxyeleotris marmorata': 'Marble goby',
  'Pachymetopon blochii': 'Hottentot seabream',
  'Pagellus acarne': 'Axillary seabream',
  'Pagellus erythrinus': 'Common pandora',
  'Pagrus auratus': 'Silver seabream',
  'Pagrus major': 'Red seabream',
  'Pagrus pagrus': 'Red porgy',
  'Pangasianodon hypophthalmus': 'Striped catfish',
  'Panulirus argus': 'Caribbean Spiny Lobster',
  'Paracanthurus hepatus': 'Palette surgeonfish',
  'Parachromis dovii': 'Guapote',
  'Parachromis managuensis': 'Jaguar guapote',
  'Paralabrax clathratus': 'Kelp bass',
  'Paralabrax maculatofasciatus': 'Spotted sand bass',
  'Paralabrax nebulifer': 'Barred sand bass',
  'Paralichthys albigutta': 'Gulf flounder',
  'Paralichthys californicus': 'California flounder',
  'Paralichthys dentatus': 'Summer flounder',
  'Paralichthys lethostigma': 'Southern flounder',
  'Parapercis colias': 'New Zealand blue cod',
  'Parupeneus cyclostomus': 'Gold-saddle goatfish',
  'Parupeneus indicus': 'Indian goatfish',
  'Parupeneus multifasciatus': 'Manybar goatfish',
  'Parupeneus spilurus': 'Blackspot goatfish',
  'Pelmatolapia mariae': 'Spotted tilapia',
  'Peprilus triacanthus': 'Atlantic butterfish',
  'Perca flavescens': 'Yellow perch',
  'Perca fluviatilis': 'European perch',
  'Percalates novemaculeatus': 'Australian bass',
  'Percina caprodes': 'Logperch',
  'Percopsis omiscomaycus': 'Trout-perch',
  'Phenacobius mirabilis': 'Suckermouth minnow',
  'Phoxinus phoxinus': 'Eurasian minnow',
  'Phractocephalus hemioliopterus': 'Redtail catfish',
  'Piaractus brachypomus': 'Pirapitinga',
  'Pimelodus maculatus': 'Bagre amarillo',
  'Pimephales notatus': 'Bluntnose minnow',
  'Pimephales promelas': 'Fathead minnow',
  'Pimephales vigilax': 'Bullhead minnow',
  'Platax boersii': 'Golden spadefish',
  'Platax teira': 'Longfin batfish',
  'Platichthys flesus': 'European flounder',
  'Platichthys stellatus': 'Starry flounder',
  'Platycephalus bassensis': 'Southern sand flathead',
  'Platycephalus caeruleopunctatus': 'Blue-spotted flathead',
  'Platycephalus fuscus': 'Dusky flathead',
  'Platycephalus indicus': 'Bartail flathead',
  'Platyrhinoidis triseriata': 'Thornback guitarfish',
  'Plectropomus laevis': 'Blacksaddled coralgrouper',
  'Plectropomus leopardus': 'Leopard coralgrouper',
  'Plectropomus maculatus': 'Spotted coralgrouper',
  'Pleuronectes platessa': 'European plaice',
  'Plotosus lineatus': 'Striped eel catfish',
  'Poecilia reticulata': 'Guppy',
  'Pogonias cromis': 'Black drum',
  'Pollachius pollachius': 'Pollack',
  'Pollachius virens': 'Saithe',
  'Polyodon spathula': 'Mississippi paddlefish',
  'Polyprion americanus': 'Wreckfish',
  'Pomacanthus arcuatus': 'Gray angelfish',
  'Pomacanthus paru': 'French angelfish',
  'Pomacentrus auriventris': 'Goldbelly damsel',
  'Pomadasys commersonnii': 'Smallspotted grunter',
  'Pomadasys kaakan': 'Javelin grunter',
  'Pomatomus saltatrix': 'Bluefish',
  'Pomatoschistus minutus': 'Sand goby',
  'Pomoxis annularis': 'White crappie',
  'Pomoxis nigromaculatus': 'Black crappie',
  'Poroderma africanum': 'Striped catshark',
  'Potamotrygon motoro': 'South American freshwater stingray',
  'Priacanthus arenatus': 'Atlantic bigeye',
  'Priolepis hipoliti': 'Rusty goby',
  'Prionace glauca': 'Blue shark',
  'Prionotus carolinus': 'Northern searobin',
  'Prionotus evolans': 'Striped searobin',
  'Prosopium williamsoni': 'Mountain whitefish',
  'Pseudobatos productus': 'Shovelnose guitarfish',
  'Pseudolabrus guentheri': 'Günther\'s wrasse',
  'Pseudoplatystoma fasciatum': 'Tiger shovelnose catfish',
  'Pseudopleuronectes americanus': 'Winter flounder',
  'Pseudupeneus maculatus': 'Spotted goatfish',
  'Pterois volitans': 'Red lionfish',
  'Pteroplatytrygon violacea': 'Pelagic stingray',
  'Pterygoplichthys gibbiceps': 'Leopard pleco',
  'Pterygoplichthys multiradiatus': 'Orinoco sailfin catfish',
  'Pterygoplichthys pardalis': 'Amazon sailfin catfish',
  'Ptychocheilus grandis': 'Sacramento pikeminnow',
  'Ptychocheilus oregonensis': 'Northern pikeminnow',
  'Pygocentrus nattereri': 'Red piranha',
  'Pylodictis olivaris': 'Flathead catfish',
  'Quassiremus ascensionis': 'Blackspotted snake eel',
  'Rachycentron canadum': 'Cobia',
  'Raja clavata': 'Thornback ray',
  'Raja miraletus': 'Brown ray',
  'Rastrelliger kanagurta': 'Indian mackerel',
  'Rhabdosargus holubi': 'Cape stumpnose',
  'Rhabdosargus sarba': 'Goldlined seabream',
  'Rhamdia quelen': 'South American catfish',
  'Rhincodon typus': 'Whale shark',
  'Rhinecanthus rectangulus': 'Wedge-tail triggerfish',
  'Rhinichthys atratulus': 'Blacknose dace',
  'Rhinichthys cataractae': 'Longnose dace',
  'Rhinichthys obtusus': 'Western blacknose dace',
  'Rhinoptera bonasus': 'Cownose ray',
  'Rhizoprionodon terraenovae': 'Atlantic sharpnose shark',
  'Rhomboplites aurorubens': 'Vermilion snapper',
  'Rutilus rutilus': 'Roach',
  'Rypticus saponaceus': 'Greater soapfish',
  'Salminus brasiliensis': 'Golden Dorado',
  'Salmo marmoratus': 'Marble Trout',
  'Salmo salar': 'Atlantic salmon',
  'Salmo trutta': 'Brown trout',
  'Salmo trutta X Salvelinus fontinalis': 'Tiger Trout',
  'Salvelinus alpinus': 'Arctic char',
  'Salvelinus confluentus': 'Bull trout',
  'Salvelinus fontinalis': 'Brook trout',
  'Salvelinus malma': 'Dolly Varden',
  'Salvelinus namaycush': 'Lake trout',
  'Salvelinus namaycush X Salvelinus fontinalis': 'Splake',
  'Sander canadensis': 'Sauger',
  'Sander lucioperca': 'Pike-perch',
  'Sander vitreus': 'Walleye',
  'Sander vitreus X Sander canadensis': 'Saugeye',
  'Sarda australis': 'Australian bonito',
  'Sarda orientalis': 'Striped bonito',
  'Sarda sarda': 'Atlantic bonito',
  'Sarotherodon melanotheron': 'Blackchin tilapia',
  'Sarpa salpa': 'Salema',
  'Scaphirhynchus platorynchus': 'Shovelnose sturgeon',
  'Scardinius erythrophthalmus': 'Rudd',
  'Scarus coeruleus': 'Blue parrotfish',
  'Scarus ghobban': 'Blue-barred parrotfish',
  'Scarus guacamaia': 'Rainbow parrotfish',
  'Scarus iseri': 'Striped parrotfish',
  'Scarus niger': 'Dusky parrotfish',
  'Scarus psittacus': 'Common parrotfish',
  'Scarus taeniopterus': 'Princess parrotfish',
  'Scarus vetula': 'Queen parrotfish',
  'Scatophagus argus': 'Spotted scat',
  'Sciaenops ocellatus': 'Red drum (Red Fish)',
  'Scomber australasicus': 'Blue mackerel',
  'Scomber colias': 'Atlantic chub mackerel',
  'Scomber japonicus': 'Chub mackerel',
  'Scomber scombrus': 'Atlantic mackerel',
  'Scomberoides commersonnianus': 'Talang queenfish',
  'Scomberoides lysan': 'Doublespotted queenfish',
  'Scomberomorus cavalla': 'King mackerel',
  'Scomberomorus commerson': 'Narrow-barred Spanish mackerel',
  'Scomberomorus maculatus': 'Atlantic Spanish mackerel',
  'Scomberomorus munroi': 'Australian spotted mackerel',
  'Scomberomorus queenslandicus': 'Queensland school mackerel',
  'Scomberomorus regalis': 'Cero',
  'Scomberomorus sierra': 'Pacific sierra',
  'Scorpaena guttata': 'California scorpionfish',
  'Scorpaena porcus': 'Black scorpionfish',
  'Scorpaenichthys marmoratus': 'Cabezon',
  'Scorpis lineolata': 'Silver sweep',
  'Scyliorhinus canicula': 'Lesser spotted dogfish',
  'Scyliorhinus stellaris': 'Nursehound',
  'Sebastes auriculatus': 'Brown rockfish',
  'Sebastes caurinus': 'Copper rockfish',
  'Sebastes fasciatus': 'Acadian redfish',
  'Sebastes maliger': 'Quillback rockfish',
  'Sebastes melanops': 'Black rockfish',
  'Sebastes miniatus': 'Vermilion rockfish',
  'Sebastes mystinus': 'Blue rockfish',
  'Sebastes pinniger': 'Canary rockfish',
  'Sebastes rastrelliger': 'Grass rockfish',
  'Sebastes ruberrimus': 'Yelloweye rockfish',
  'Selar crumenophthalmus': 'Bigeye scad',
  'Selene setapinnis': 'Atlantic moonfish',
  'Selene vomer': 'Lookdown',
  'Semicossyphus pulcher': 'California sheephead',
  'Semotilus atromaculatus': 'Creek chub',
  'Semotilus corporalis': 'Fallfish',
  'sepia officinalis': 'Cuttlefish',
  'Seriola dumerili': 'Greater amberjack',
  'Seriola hippos': 'Samson fish',
  'Seriola lalandi': 'Yellowtail amberjack',
  'Seriola rivoliana': 'Almaco Jack',
  'Serranochromis robustus': 'Yellow-belly bream',
  'Serranus cabrilla': 'Comber',
  'Serranus scriba': 'Painted comber',
  'Serrasalmus rhombeus': 'Redeye piranha',
  'Siganus guttatus': 'Orange-spotted spinefoot',
  'Siganus vulpinus': 'Foxface',
  'Sillaginodes punctatus': 'King George whiting',
  'Sillago ciliata': 'Sand sillago',
  'Sillago schomburgkii': 'Yellowfin whiting',
  'Silurus glanis': 'Wels catfish',
  'Sparisoma atomarium': 'Greenblotch parrotfish',
  'Sparisoma aurofrenatum': 'Redband parrotfish',
  'Sparisoma chrysopterum': 'Redtail parrotfish',
  'Sparisoma cretense': 'Parrotfish',
  'Sparisoma rubripinne': 'Redfin parrotfish',
  'Sparisoma viride': 'Stoplight parrotfish',
  'Sparodon durbanensis': 'Musselcracker seabream',
  'Sparus aurata': 'Gilthead seabream',
  'Sphoeroides maculatus': 'Northern puffer',
  'Sphoeroides nephelus': 'Southern puffer',
  'Sphoeroides pachygaster': 'Blunthead puffer',
  'Sphoeroides parvus': 'Least puffer',
  'Sphoeroides spengleri': 'Bandtail puffer',
  'Sphoeroides testudineus': 'Checkered puffer',
  'Sphyraena argentea': 'Pacific barracuda',
  'Sphyraena barracuda': 'Great barracuda',
  'Sphyraena guachancho': 'Guachanche barracuda',
  'Sphyraena jello': 'Pickhandle barracuda',
  'Sphyraena novaehollandiae': 'Australian barracuda',
  'Sphyraena obtusata': 'Obtuse barracuda',
  'Sphyraena qenie': 'Blackfin barracuda',
  'Sphyraena sphyraena': 'European barracuda',
  'Sphyraena viridensis': 'Yellowmouth barracuda',
  'Sphyrna lewini': 'Scalloped hammerhead',
  'Sphyrna mokarran': 'Great hammerhead',
  'Sphyrna tiburo': 'Bonnethead',
  'Sphyrna zygaena': 'Smooth hammerhead',
  'Spondyliosoma cantharus': 'Black seabream',
  'Squalus acanthias': 'Picked dogfish',
  'Stegastes adustus': 'Dusky damselfish',
  'Stegastes diencaeus': 'Longfin damselfish',
  'Stegastes leucostictus': 'Beaugregory',
  'Stegastes partitus': 'Bicolor damselfish',
  'Stegastes variabilis': 'Caribbean Cocoa Damselfish',
  'Stenotomus chrysops': 'Scup',
  'Stereolepis gigas': 'Giant seabass',
  'Strongylura marina': 'Atlantic needlefish',
  'Symphodus cinereus': 'Grey wrasse',
  'Symphodus melops': 'Corkwing wrasse',
  'Symphodus tinca': 'East Atlantic peacock wrasse',
  'Synchiropus splendidus': 'Mandarinfish',
  'Synodus foetens': 'Inshore lizardfish',
  'Synodus saurus': 'Atlantic lizardfish',
  'Taeniura lymma': 'Ribbontail stingray',
  'Tandanus tandanus': 'Australian freshwater catfish',
  'Tautoga onitis': 'Tautog',
  'Tautogolabrus adspersus': 'Cunner',
  'Terapon jarbua': 'Jarbua terapon',
  'Thalassoma bifasciatum': 'Bluehead',
  'Thalassoma duperrey': 'Saddle wrasse',
  'Thalassoma lunare': 'Moon wrasse',
  'Thalassoma pavo': 'Ornate wrasse',
  'Thalassoma purpureum': 'Surge wrasse',
  'Thunnus alalunga': 'Albacore',
  'Thunnus albacares': 'Yellowfin tuna',
  'Thunnus atlanticus': 'Blackfin tuna',
  'Thunnus obesus': 'Bigeye tuna',
  'Thunnus orientalis': 'Pacific bluefin tuna',
  'Thunnus thynnus': 'Atlantic bluefin tuna',
  'Thunnus tonggol': 'Longtail tuna',
  'Thymallus arcticus': 'Arctic grayling',
  'Thymallus thymallus': 'Grayling',
  'Thyrsites atun': 'Snoek',
  'Tilapia sparrmanii': 'Banded tilapia',
  'Tinca tinca': 'Tench',
  'Torquigener pleurogramma': 'Weeping toado',
  'Toxotes jaculatrix': 'Banded archerfish',
  'Trachinotus baillonii': 'Small spotted dart',
  'Trachinotus blochii': 'Snubnose pompano',
  'Trachinotus carolinus': 'Florida pompano',
  'Trachinotus coppingeri': 'Swallowtail dart',
  'Trachinotus falcatus': 'Permit',
  'Trachinotus goodei': 'Great pompano',
  'Trachinotus ovatus': 'Pompano',
  'Trachinus draco': 'Greater weever',
  'Trachurus mediterraneus': 'Mediterranean horse mackerel',
  'Trachurus novaezelandiae': 'Yellowtail horse mackerel',
  'Trachurus symmetricus': 'Pacific jack mackerel',
  'Trachurus trachurus': 'Atlantic horse mackerel',
  'Triaenodon obesus': 'Whitetip reef shark',
  'Triakis megalopterus': 'Sharptooth houndshark',
  'Triakis semifasciata': 'Leopard shark',
  'Trichiurus lepturus': 'Largehead hairtail',
  'Trinectes maculatus': 'Hogchoker',
  'Trisopterus luscus': 'Pouting',
  'Trygonoptera testacea': 'Common stingaree',
  'Trygonorrhina fasciata': 'Eastern fiddler ray',
  'Tylosurus crocodilus': 'Hound needlefish',
  'Umbra limi': 'Central mudminnow',
  'Variola louti': 'Yellow-edged lyretail',
  'Wallago attu': 'Wallago',
  'Xanthichthys ringens': 'Sargassum triggerfish',
  'Xiphias gladius': 'Swordfish',
  'Xyrichtys novacula': 'Pearly razorfish',
  'Zebrasoma flavescens': 'Yellow tang',
}

/** English common name for a scientific binomial, or undefined if unmapped. */
export function commonName(scientific: string): string | undefined {
  return COMMON_NAMES[scientific]
}

/** Every common name, for a species input's <datalist>. */
export const COMMON_SPECIES_NAMES: string[] = Array.from(new Set(Object.values(COMMON_NAMES))).sort()
